import React from "react";
import classnames from "classnames";

import { convertToFormattedId } from "@graffer-inc/cloud-gov-core";
import {
  Checkbox,
  CheckboxProps,
  InformationBanner,
} from "@graffer-inc/console-design-kit";

import { matchType } from "../../../../../application/domain/base/base-type";
import { BaseModal } from "../../../../atoms/base-modal";
import { SmartApplyR } from "../../../../pages/smart-apply/route";
import { ProcedureEntity } from "../../../../../application/domain/procedures/procedure-entity";
import { ApplicationEntity } from "../../../../../application/domain/application/application-entity";
import {
  BulkDLFilesResult,
  BulkDLFilesTarget,
  BulkDLFilesTargetLabel,
} from "../../../../../application/domain/application/bulk-download-files";
import { useBulkDownloadFiles } from "./use-bulk-download-files";

export type BulkDownloadFilesModalContainerProps = {
  procedure: ProcedureEntity;
  applications: ApplicationEntity[];
  onClose: () => void;
};

export const BulkDownloadFilesModalContainer: React.FC<
  BulkDownloadFilesModalContainerProps
> = ({ procedure, applications, onClose }) => {
  const props = useBulkDownloadFiles(procedure, applications);
  return (
    <BulkDownloadFilesModal
      procedureName={procedure.name}
      applicationCount={applications.length}
      onClose={onClose}
      {...props}
    />
  );
};

export type BulkDownloadFilesModalProps = {
  procedureName: string;
  applicationCount: number;
  targets: BulkDLFilesTarget[];
  onToggleTarget: (target: BulkDLFilesTarget) => void;
  message: string;
  options: CheckboxProps["options"];
  result: BulkDLFilesResult;
  onClose: () => void;
  onExecute: () => void;
};

export const BulkDownloadFilesModal: React.FC<BulkDownloadFilesModalProps> = ({
  procedureName,
  applicationCount,
  targets,
  onToggleTarget,
  options,
  message,
  result,
  onClose,
  onExecute,
}) => {
  return (
    <BaseModal title="ダウンロード" isActive onClose={onClose}>
      {matchType(result.status, {
        CONFIRM: (
          <>
            <section className="modal-card-body">
              {!!message && (
                <div className="field">
                  <InformationBanner message={message} />
                </div>
              )}
              <div className="field">
                <p>
                  申請者が添付したファイルや、申請内容をもとに作成した帳票ファイルを一括でダウンロードします。
                </p>
              </div>
              <div className="field">
                <label htmlFor="name" className="label">
                  対象の手続き名
                </label>
                <div className="control">{procedureName}</div>
              </div>
              <div className="field">
                <label htmlFor="name" className="label">
                  対象の申請数
                </label>
                <div className="control">{applicationCount}</div>
              </div>
              {!!options.length && (
                <div className="field">
                  <Checkbox
                    values={targets}
                    options={options}
                    onChange={(e) =>
                      onToggleTarget(e.target.value as BulkDLFilesTarget)
                    }
                    label={"ダウンロードするファイルの種類"}
                    description={
                      "ダウンロードするファイルの種類を選択してください。"
                    }
                    required
                  />
                </div>
              )}
            </section>
            <footer className="modal-card-foot">
              <button
                className={classnames("button is-fullwidth")}
                onClick={onClose}
              >
                閉じる
              </button>
              <button
                className={classnames("button is-info is-fullwidth")}
                onClick={onExecute}
                data-for-rpa-id={"output-all-files"}
                disabled={targets.length === 0}
              >
                ダウンロード
              </button>
            </footer>
          </>
        ),
        FETCHING: (
          <>
            <section className="modal-card-body">
              <>
                <p className="has-text-centered">ファイルをダウンロード中</p>
                <p className="has-text-left">
                  長時間経過しても完了しない場合は、時間をおいてから再度お試しください。
                </p>
                <progress
                  className="progress is-info"
                  value={result.fetchedCount}
                  max={applicationCount}
                />
                <p className="help">
                  閉じるボタンをクリックしてもダウンロードは続行されます。
                </p>
              </>
            </section>
            <footer className="modal-card-foot">
              <button
                className={classnames("button is-fullwidth")}
                onClick={onClose}
              >
                閉じる
              </button>
            </footer>
          </>
        ),
        ZIPPING: (
          <>
            <section className="modal-card-body">
              <>
                <p className="has-text-centered">ファイルを圧縮中</p>
                <progress
                  className="progress is-info"
                  value={result.zippedPercent}
                  max="100"
                />
                <p className="help">
                  閉じるボタンをクリックしても圧縮処理は続行されます。
                </p>
              </>
            </section>
            <footer className="modal-card-foot">
              <button
                className={classnames("button is-fullwidth")}
                onClick={onClose}
              >
                閉じる
              </button>
            </footer>
          </>
        ),
        DONE: (
          <>
            <section className="modal-card-body">
              <p>
                {applicationCount}件中{applicationCount - result.errors.length}
                件のダウンロードが完了しました。
              </p>
              {result.errors.length > 0 && (
                <>
                  <p>
                    {result.errors.length}
                    件のダウンロードに失敗しました。
                  </p>
                  <table className="table is-fullwidth">
                    <thead>
                      <tr>
                        <th style={{ width: 150 }}>申請番号</th>
                        <th style={{ width: 120 }}>ファイルの種類</th>
                        <th>エラー内容</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errors.map((err) => (
                        <tr key={err.id}>
                          <td>
                            <a
                              target="_blank"
                              href={SmartApplyR.ApplicationDetail.link({
                                id: err.id,
                              })}
                              rel="noopener noreferrer"
                            >
                              {convertToFormattedId(err.id)}
                            </a>
                          </td>
                          <td>{BulkDLFilesTargetLabel(err.target)}</td>
                          <td>{err.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </section>
            <footer className="modal-card-foot">
              <button
                className={classnames("button is-fullwidth")}
                onClick={onClose}
              >
                閉じる
              </button>
            </footer>
          </>
        ),
      })}
    </BaseModal>
  );
};
